import { useEffect, useState } from "react";
import { api } from "../api/client";
import { getPageActivity, subscribePageActivity } from "../api/pageActivity";
import { operationPresentation } from "../lib/operationPresentation";
import { OperationStatus } from "../components/OperationStatus";
import type { Operation, OperationsResponse } from "../types";

const terminal = (state: string) => state === "completed" || state === "cancelled";
/**
 * States nothing but a server event or an operator action can leave. Polling them every two
 * seconds changed nothing and kept a stalled close requesting the whole journal for a day.
 * Deliberately not folded into `terminal`: this screen must still accept a server update, or it
 * would freeze on `blocked` even after the operation is resumed from another screen.
 */
const stalled = (state: string) =>
  terminal(state) || ["failed", "blocked", "needs-attention"].includes(state);

export function AcceptedOperation({ initialOperation }: { initialOperation: Operation }) {
  const [operation, setOperation] = useState(initialOperation);
  const [updateFailed, setUpdateFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let currentState = initialOperation.state;
    let inFlight = false;
    let timer = 0;
    let requestIdentity = 0;
    let controller: AbortController | null = null;

    const refresh = async () => {
      const page = getPageActivity();
      if (!live || !page.active || inFlight || terminal(currentState)) return;
      window.clearTimeout(timer);
      inFlight = true;
      const identity = ++requestIdentity;
      const activation = page.activation;
      const requestController = new AbortController();
      controller = requestController;
      const cancelled = () => {
        const currentPage = getPageActivity();
        return (
          !live ||
          identity !== requestIdentity ||
          requestController.signal.aborted ||
          !currentPage.active ||
          currentPage.activation !== activation
        );
      };
      try {
        const response = await api<OperationsResponse>("/operations", {
          signal: requestController.signal,
          cache: "no-store",
        });
        if (cancelled()) return;
        const updated = response.items.find((item) => item.id === initialOperation.id);
        if (!updated) throw new Error("Accepted operation missing from operation history");
        currentState = updated.state;
        setOperation(updated);
        setUpdateFailed(false);
      } catch (reason) {
        if (!cancelled() && !(reason instanceof DOMException && reason.name === "AbortError"))
          setUpdateFailed(true);
      } finally {
        if (identity === requestIdentity) {
          inFlight = false;
          controller = null;
          if (!cancelled() && !stalled(currentState))
            timer = window.setTimeout(() => {
              void refresh();
            }, 2_000);
        }
      }
    };
    const onServerEvent = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (detail === "operations" || detail === "message") void refresh();
    };
    window.addEventListener("auto-pools:server-event", onServerEvent);
    const unsubscribe = subscribePageActivity(() => {
      window.clearTimeout(timer);
      if (!getPageActivity().active) {
        requestIdentity += 1;
        inFlight = false;
        controller?.abort();
        controller = null;
      } else {
        void refresh();
      }
    });
    void refresh();
    return () => {
      live = false;
      requestIdentity += 1;
      window.clearTimeout(timer);
      controller?.abort();
      unsubscribe();
      window.removeEventListener("auto-pools:server-event", onServerEvent);
    };
  }, [initialOperation.id, initialOperation.state]);

  const view = operationPresentation(operation);
  return (
    <div className="accepted-command">
      <span
        className={`accepted-icon ${view.tone === "green" ? "success" : view.tone === "muted" ? "waiting" : "attention"}`}
        aria-hidden="true"
      >
        {view.icon}
      </span>
      <h3>{view.title}</h3>
      {operation.state === "cancelled" ? (
        <p>Check My positions for the separate cancellation operation before setting up again.</p>
      ) : operation.state === "completed" ? (
        <p>Your position is open.</p>
      ) : (
        <p>The latest recorded status is shown below and updates automatically.</p>
      )}
      {updateFailed && (
        <p className="inline-alert error" role="alert">
          Status could not be updated. Showing last known status.
        </p>
      )}
      <OperationStatus operation={operation} />
    </div>
  );
}
